// src/components/PaymentAccounts/PaymentAccountsManager.jsx
// Gestor de cuentas de cobro (presets) — vive en el perfil del usuario.
import React, { useState, useEffect, useCallback } from 'react';
import { FiCreditCard, FiPlus, FiEdit2, FiTrash2, FiCheck, FiInfo } from 'react-icons/fi';
import { listAccounts, createAccount, updateAccount, deleteAccount } from '../../lib/paymentAccounts';
import './PaymentAccounts.css';

const EMPTY = { alias: '', method: '', holder: '', number: '', details: '' };

export default function PaymentAccountsManager() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(null);   // null = cerrado; {id?, ...} = abierto
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setAccounts(await listAccounts());
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openNew  = () => { setForm({ ...EMPTY }); setError(''); };
  const openEdit = (a) => { setForm({ id: a.id, alias: a.alias, method: a.method, holder: a.holder || '', number: a.number, details: a.details || '' }); setError(''); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.alias.trim() || !form.method.trim() || !form.number.trim()) {
      setError('Completá alias, método y número.');
      return;
    }
    setBusy(true); setError('');
    try {
      if (form.id) await updateAccount(form.id, form);
      else         await createAccount(form);
      setForm(null);
      await refresh();
    } catch (e) { setError(e.message || 'Error al guardar'); }
    setBusy(false);
  };

  const doDelete = async (id) => {
    setBusy(true);
    try { await deleteAccount(id); await refresh(); } catch {}
    setBusy(false); setConfirmDel(null);
  };

  return (
    <section className="pa-card">
      <div className="pa-head">
        <h3 className="pa-title"><FiCreditCard className="pa-title-ic" /> Mis cuentas de cobro</h3>
        {!form && <button className="pa-add-btn" onClick={openNew}><FiPlus /> Agregar</button>}
      </div>
      <p className="pa-sub">Guardá tus cuentas/billeteras una vez y enviálas con un toque en el chat de cada trade.</p>

      {/* Formulario alta/edición */}
      {form && (
        <div className="pa-form">
          <div className="pa-form-row">
            <label className="pa-f"><span>Alias *</span><input value={form.alias} onChange={(e) => set('alias', e.target.value)} placeholder="Ej: BAC principal" maxLength={40} autoFocus /></label>
            <label className="pa-f"><span>Banco / Método *</span><input value={form.method} onChange={(e) => set('method', e.target.value)} placeholder="BAC, SINPE Móvil, Nequi, PayPal…" maxLength={40} /></label>
          </div>
          <div className="pa-form-row">
            <label className="pa-f"><span>Titular</span><input value={form.holder} onChange={(e) => set('holder', e.target.value)} placeholder="Nombre del titular" maxLength={80} /></label>
            <label className="pa-f"><span>Número / Identificador *</span><input value={form.number} onChange={(e) => set('number', e.target.value)} placeholder="N° de cuenta, IBAN, teléfono, email…" maxLength={80} /></label>
          </div>
          <label className="pa-f"><span>Detalle <span className="pa-opt">(opcional)</span></span><input value={form.details} onChange={(e) => set('details', e.target.value)} placeholder="Tipo de cuenta, cédula, SWIFT…" maxLength={120} /></label>
          {error && <div className="pa-error"><FiInfo /> {error}</div>}
          <div className="pa-form-actions">
            <button className="pa-cancel" onClick={() => setForm(null)}>Cancelar</button>
            <button className="pa-save" onClick={save} disabled={busy}><FiCheck /> {form.id ? 'Guardar' : 'Agregar cuenta'}</button>
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="pa-empty">Cargando cuentas…</div>
      ) : accounts.length === 0 && !form ? (
        <div className="pa-empty">
          <FiCreditCard className="pa-empty-ic" />
          <p>Todavía no guardaste cuentas. Agregá una para enviarla rápido en tus trades.</p>
        </div>
      ) : (
        <div className="pa-list">
          {accounts.map((a) => (
            <div key={a.id} className="pa-item">
              <div className="pa-item-main">
                <div className="pa-item-top">
                  <strong className="pa-item-alias">{a.alias}</strong>
                  <span className="pa-item-method">{a.method}</span>
                </div>
                <div className="pa-item-num fin-num">{a.number}</div>
                {(a.holder || a.details) && <div className="pa-item-meta">{[a.holder, a.details].filter(Boolean).join(' · ')}</div>}
              </div>
              <div className="pa-item-acts">
                <button className="pa-act" onClick={() => openEdit(a)} title="Editar"><FiEdit2 /></button>
                <button className="pa-act del" onClick={() => setConfirmDel(a.id)} title="Borrar"><FiTrash2 /></button>
              </div>
              {confirmDel === a.id && (
                <div className="pa-confirm">
                  <span>¿Borrar esta cuenta?</span>
                  <div>
                    <button className="pa-confirm-no" onClick={() => setConfirmDel(null)}>No</button>
                    <button className="pa-confirm-yes" onClick={() => doDelete(a.id)} disabled={busy}>Sí, borrar</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
